import React, { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import {
  SignaturePad,
  signatureAssetFromUpload,
  type SignatureAsset,
  type SignatureValue,
} from '../../components/signature';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  normalizePaginatedListResponse,
  type PaginatedApiResponse,
} from '../../services/paginatedList';
import {
  eFormFileDownloadPath,
  uploadEFormFiles,
} from '../../services/mutations';
import type { EFormFieldSchema } from './schema';

type VesselStampRow = {
  id: number;
  vesselName: string;
  vesselAbrv?: string | null;
  stampStorageKey?: string | null;
  stampOriginalFilename?: string | null;
};

export const EFormSignatureField: React.FC<{
  field: EFormFieldSchema;
  value: unknown;
  onChange: (value: SignatureValue | null) => void;
  readOnly?: boolean;
  error?: string;
}> = ({ field, value, onChange, readOnly = false, error }) => {
  const allowStamp = Boolean(field.signatureWithStamp);

  const stampListQuery = useMemo(() => {
    if (!allowStamp) return null;
    return `/vessels${buildListQuery(undefined, 1, 200, {
      sortKey: 'vesselName',
      sortDirection: 'asc',
      filters: { filters: { hasStamp: ['true'], isActive: ['true'] } },
    })}`;
  }, [allowStamp]);

  const { data: vesselsRaw } = useSWR<PaginatedApiResponse<VesselStampRow> | VesselStampRow[]>(
    stampListQuery,
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselStampOptions = useMemo(() => {
    const rows = normalizePaginatedListResponse<VesselStampRow>(vesselsRaw, 1, 200).data;
    return rows
      .filter((v) => v.stampStorageKey)
      .map((v) => ({
        value: v.id,
        label: v.vesselAbrv ? `${v.vesselName} (${v.vesselAbrv})` : v.vesselName,
      }));
  }, [vesselsRaw]);

  const upload = useCallback(async (file: File): Promise<SignatureAsset> => {
    const [meta] = await uploadEFormFiles([file]);
    return signatureAssetFromUpload(meta);
  }, []);

  const downloadPath = useCallback(
    (storageKey: string) => eFormFileDownloadPath(storageKey),
    [],
  );

  const onSelectVesselStamp = useCallback(async (vesselId: number): Promise<SignatureAsset> => {
    const res = await api.get(`/vessels/${vesselId}/stamp`, {
      responseType: 'blob',
      headers: { Accept: 'image/*,application/octet-stream' },
    });
    const blob = res.data as Blob;
    const type = (blob.type || '').toLowerCase();
    if (!blob?.size || type.includes('json') || type.includes('text/')) {
      throw new Error('Vessel has no stamp image.');
    }
    const mime = type.startsWith('image/') ? type : 'image/png';
    const ext = mime === 'image/jpeg' ? 'jpg' : mime === 'image/webp' ? 'webp' : 'png';
    const file = new File([blob], `vessel-${vesselId}-stamp.${ext}`, { type: mime });
    return upload(file);
  }, [upload]);

  return (
    <SignaturePad
      value={value}
      onChange={onChange}
      upload={upload}
      downloadPath={downloadPath}
      readOnly={readOnly || Boolean(field.readOnly)}
      required={Boolean(field.required)}
      allowStamp={allowStamp}
      vesselStampOptions={allowStamp ? vesselStampOptions : undefined}
      onSelectVesselStamp={allowStamp ? onSelectVesselStamp : undefined}
      error={error}
      height={168}
    />
  );
};

export default EFormSignatureField;
